/* eslint-disable */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import Warp from 'warpjs';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import generateMeshPoints from './utils/generateMeshPoints';
import iterpritateSmoothness from './utils/iterpritateSmoothness';
import moveCanvasHandler from './utils/moveCanvas';
import saveResultHandler from './utils/saveResults';
import { testSVG } from './utils/svg-test-string';
import './styles/styles.scss'; // Your SCSS imports
import flattenSvgTransforms from './utils/flattenSvgTransforms';
import { useDesignPatternStudio } from '../../DesignPatternStudioContext';
import { ACTIONS } from '../../reducers/studioReducer';
import ConfirmDialog from '../../../Shared/ConfirmDialog';

const WarpSvg = ({ pattern, ringPath, meshComplexity, interpolation, savedControlPoints, savedPatternPosition, savedPatternScale, savedHistory, savedHistoryIndex, onSave, }) => {
  const { dispatch } = useDesignPatternStudio();
  const appRef = useRef(null);
  const svgContainerRef = useRef(null);
  const svgElementRef = useRef(null);
  const svgControlRef = useRef(null);
  const zoomElementRef = useRef(null);
  const controlPathRef = useRef(null);
  const svgRingPathRef = useRef(null);
  const ringPathRef = useRef(null);

  const [svgString, setSvgString] = useState(pattern || testSVG);
  const [showSettings, setShowSettings] = useState(true);
  const [complexityLevel, setComplexityLevel] = useState(meshComplexity);
  const [interpolationLevel, setInterpolationLevel] = useState(interpolation);
  const complexityLevelRef = useRef(meshComplexity);
  const interpolationLevelRef = useRef(interpolation);
  const [showOriginalBox, setShowOriginalBox] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const draggableControlPointsRef = useRef([]);
  const warpRef = useRef(null);
  const controlPointsRef = useRef(null);
  const widthRef = useRef(500);
  const heightRef = useRef(500);
  const viewBoxScaleRef = useRef(1);
  const meshBoundsRef = useRef(null);
  const meshEpsilonRef = useRef(0);
  const zoomRef = useRef(1);

  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const patternDragHistoryTimeoutRef = useRef(null);
  const patternScaleHistoryTimeoutRef = useRef(null);
  const isRestoringFromHistoryRef = useRef(false);

  // Pattern dragging state and refs
  const [isDragMode, setIsDragMode] = useState(false);
  const [patternPosition, setPatternPosition] = useState({ x: 0, y: 0 });
  const patternPositionRef = useRef({ x: 0, y: 0 });
  const isDraggingPattern = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });

  // Pattern scaling state and refs
  const [isScaleMode, setIsScaleMode] = useState(false);
  const [patternScale, setPatternScale] = useState(1);
  const patternScaleRef = useRef(1);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const CIRCLE_RADIUS = 5;

  const getCombinedScale = () => {
    return viewBoxScaleRef.current * zoomRef.current * patternScaleRef.current;
  };

  useEffect(() => {
    if (savedPatternPosition) {
      setPatternPosition(savedPatternPosition);
    }
  }, [savedPatternPosition]);

  // Keep patternPositionRef in sync with state (for use in callbacks / future use)
  useEffect(() => {
    patternPositionRef.current = patternPosition;
  }, [patternPosition]);

  useEffect(() => {
    if (savedPatternScale !== undefined && savedPatternScale !== null) {
      setPatternScale(savedPatternScale);
      patternScaleRef.current = savedPatternScale;
    }
  }, [savedPatternScale]);

  // Update svgString when pattern prop changes
  useEffect(() => {
    if (pattern) {
      setSvgString(pattern);
      // Reset history when pattern changes
      historyRef.current = [];
      historyIndexRef.current = -1;
      setCanUndo(false);
      setCanRedo(false);
    }
  }, [pattern]);

  useEffect(() => {
    if (meshComplexity) {
      setComplexityLevel(meshComplexity);
      complexityLevelRef.current = meshComplexity;
    }
    if (interpolation) {
      setInterpolationLevel(interpolation);
      interpolationLevelRef.current = interpolation;
    }
  }, [meshComplexity, interpolation]);

  const updateComplexityLevel = (e) => {
    const level = Number(e.target.value);
    setComplexityLevel(level);
    complexityLevelRef.current = level;
    init(null, false, true);
  };
  const updateInterpolationLevel = (e) => {
    const level = Number(e.target.value);
    setInterpolationLevel(level);
    interpolationLevelRef.current = level;
    init(null, false, true);
  };

  // Update ring path when prop changes
  // Ring path determines CONTAINER dimensions (viewport/frame)
  useEffect(() => {
    if (ringPath && svgRingPathRef.current && ringPathRef.current) {
      /**
       *
       * Initially ring path is empty and once ring path which received
       * set ring path and stroke properties.
       * Later it get rendered and get ring path dimensions and set on svg-ring-path.
       *
       */
      ringPathRef.current.setAttributeNS(null, 'd', ringPath);
      ringPathRef.current.setAttributeNS(null, 'fill', 'none');
      ringPathRef.current.setAttributeNS(null, 'stroke', '#ff0000');
      ringPathRef.current.setAttributeNS(null, 'stroke-width', '2');
      ringPathRef.current.setAttributeNS(null, 'stroke-opacity', '0.5');
      ringPathRef.current.setAttributeNS(null, 'stroke-dasharray', '5,5');

      /**
       * After setting ring path above and getBBox,
       * set ring path dimensions width / height on svgRingPathRef and svgContainerRef.
       */
      setTimeout(() => {
        try {
          if (ringPathRef.current && svgRingPathRef.current && svgContainerRef.current) {
            const bbox = ringPathRef.current.getBBox();
            if (bbox && isFinite(bbox.width) && isFinite(bbox.height)) {
              const viewBox = `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`;

              svgRingPathRef.current.setAttribute('viewBox', viewBox);
              svgRingPathRef.current.setAttribute('width', bbox.width);
              svgRingPathRef.current.setAttribute('height', bbox.height);

              svgContainerRef.current.style.width = `${bbox.width}px`;
              svgContainerRef.current.style.height = `${bbox.height}px`;

            }
          }
        } catch (e) {
          console.warn('Could not set ring path dimensions:', e);
        }
      }, 0);
    }
  }, [ringPath]);

  // Register GSAP plugins
  useEffect(() => {
    gsap.registerPlugin(Draggable);
  }, []);

  const clearPatternDragHistoryTimeout = () => {
    clearTimeout(patternDragHistoryTimeoutRef.current);
    patternDragHistoryTimeoutRef.current = null;
  };

  const clearPatternScaleHistoryTimeout = () => {
    clearTimeout(patternScaleHistoryTimeoutRef.current);
    patternScaleHistoryTimeoutRef.current = null;
  };

  // Sync undo/redo button state from refs (single source of truth)
  // Index 0 = initial loaded state; undo stops at 0, redo at length-1
  const syncUndoRedoState = useCallback(() => {
    const idx = historyIndexRef.current;
    const len = historyRef.current.length;
    setCanUndo(idx > 0);
    setCanRedo(idx < len - 1);
  }, []);

  // Core: push one entry to history (shared by control-point changes and pattern-position changes).
  // When history is empty (index -1), first push becomes index 0 (initial state).
  const pushHistoryEntry = useCallback((entry) => {
    if (!entry || !Array.isArray(entry.controlPoints) || entry.controlPoints.length === 0) return;

    const controlPointsCopy = entry.controlPoints.map(p => [...p]);

    const newHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
    newHistory.push({ ...entry, controlPoints: controlPointsCopy });

    if (newHistory.length > 50) {
      newHistory.shift();
      historyIndexRef.current = newHistory.length - 1;
    } else {
      historyIndexRef.current++;
    }

    historyRef.current = newHistory;
    syncUndoRedoState();
  }, [syncUndoRedoState]);

  const pushToHistory = () => {
    if(!controlPointsRef.current || !Array.isArray(controlPointsRef.current) || controlPointsRef.current.length === 0) return;
    if(!patternPositionRef.current) return;

    const entry = {
      controlPoints: controlPointsRef.current,
      patternPosition: patternPositionRef.current,
      patternScale: patternScaleRef.current,
      meshComplexity: complexityLevelRef.current,
      interpolation: interpolationLevelRef.current,
    };
    pushHistoryEntry(entry);
  };

  // Undo handler (restores control points, pattern position, meshComplexity, interpolation from history; stops at index 0 = initial state)
  const handleUndo = useCallback(() => {
    const len = historyRef.current.length;
    if (len === 0 || historyIndexRef.current <= 0) return;

    // Cancel any pending pattern-drag/scale history save so it doesn't push after we undo
    clearPatternDragHistoryTimeout();
    clearPatternScaleHistoryTimeout();

    isRestoringFromHistoryRef.current = true;
    historyIndexRef.current--;
    const entry = historyRef.current[historyIndexRef.current];
    const previousPoints = entry?.controlPoints;
    if (entry?.patternPosition != null) {
      setPatternPosition(entry.patternPosition);
      patternPositionRef.current = entry.patternPosition;
    }
    if (entry?.patternScale != null) {
      setPatternScale(entry.patternScale);
      patternScaleRef.current = entry.patternScale;
    }
    if (entry?.meshComplexity != null) {
      setComplexityLevel(entry.meshComplexity);
      complexityLevelRef.current = entry.meshComplexity;
    }
    if (entry?.interpolation != null) {
      setInterpolationLevel(entry.interpolation);
      interpolationLevelRef.current = entry.interpolation;
    }
    init(previousPoints ?? null, true);

    syncUndoRedoState();
  }, [syncUndoRedoState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Redo handler (restores control points, pattern position, meshComplexity, interpolation from history)
  const handleRedo = useCallback(() => {
    const len = historyRef.current.length;
    if (len === 0 || historyIndexRef.current >= len - 1) return;

    // Cancel any pending pattern-drag/scale history save so it doesn't push after we redo
    clearPatternDragHistoryTimeout();
    clearPatternScaleHistoryTimeout();

    isRestoringFromHistoryRef.current = true;
    historyIndexRef.current++;
    const entry = historyRef.current[historyIndexRef.current];
    const nextPoints = entry?.controlPoints;
    if (entry?.patternPosition != null) {
      setPatternPosition(entry.patternPosition);
      patternPositionRef.current = entry.patternPosition;
    }
    if (entry?.patternScale != null) {
      setPatternScale(entry.patternScale);
      patternScaleRef.current = entry.patternScale;
    }
    if (entry?.meshComplexity != null) {
      setComplexityLevel(entry.meshComplexity);
      complexityLevelRef.current = entry.meshComplexity;
    }
    if (entry?.interpolation != null) {
      setInterpolationLevel(entry.interpolation);
      interpolationLevelRef.current = entry.interpolation;
    }
    init(nextPoints ?? null, true);

    syncUndoRedoState();
  }, [syncUndoRedoState]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Calculate and sync dimensions between pattern SVG and control SVG
   *
   * Purpose:
   * - Measures the actual rendered size of the pattern (svg-element)
   * - Applies the same width, height, and viewBox to control layer (svg-control)
   * - Stores dimensions in refs for control point generation
   *
   * Flow:
   * 1. Get pattern's bounding box (actual rendered size)
   * 2. Store dimensions in widthRef/heightRef for later use
   * 3. Set explicit width/height attributes on both SVGs (ensures same visual size)
   * 4. Copy viewBox from pattern to control (ensures same coordinate system)
   *
   * Why this matters:
   * - Control points overlay must align perfectly with pattern
   * - Both SVGs need identical coordinate space for accurate dragging
   * - Pattern renders independently, control follows its dimensions
   */
  const calculateContainerDimensions = useCallback(() => {
    if (!svgElementRef.current || !svgControlRef.current) return;
    const width = svgElementRef.current.getAttribute('width') || 0;
    const height = svgElementRef.current.getAttribute('height') || 0;

    widthRef.current = Math.round(width);
    heightRef.current = Math.round(height);

    svgElementRef.current.setAttribute('width', `${width}`);
    svgElementRef.current.setAttribute('height', `${height}`);
    svgControlRef.current.setAttribute('width', `${width}`);
    svgControlRef.current.setAttribute('height', `${height}`);

    const viewBox = svgElementRef.current.getAttribute('viewBox');
    if (viewBox) {
      svgControlRef.current.setAttribute('viewBox', viewBox);
    } else {
      svgControlRef.current.setAttribute('viewBox', `0 0 ${width} ${height}`);
    }
  }, []);

  const updateMeshBounds = useCallback(() => {
    if (!svgElementRef.current) return;
    const svg = svgElementRef.current;
    const viewBoxAttr = svg.getAttribute('viewBox');
    let vbX = 0;
    let vbY = 0;
    let vbW = 0;
    let vbH = 0;

    if (viewBoxAttr) {
      const parts = viewBoxAttr.trim().split(/[\s,]+/).map(Number);
      if (parts.length === 4 && parts.every(Number.isFinite)) {
        [vbX, vbY, vbW, vbH] = parts;
      }
    }

    if (!vbW || !vbH) {
      vbW = parseFloat(svg.getAttribute('width')) || 0;
      vbH = parseFloat(svg.getAttribute('height')) || 0;
    }

    let bbox;
    try {
      bbox = svg.getBBox();
    } catch (e) {
      console.warn('Could not read SVG bounds for mesh:', e);
      return;
    }

    if (!bbox || !isFinite(bbox.width) || !isFinite(bbox.height)) {
      return;
    }

    const tol = Math.max(0.01, Math.max(vbW, vbH) * 1e-6);
    const extendsViewBox = (
      bbox.x < vbX - tol ||
      bbox.y < vbY - tol ||
      bbox.x + bbox.width > vbX + vbW + tol ||
      bbox.y + bbox.height > vbY + vbH + tol
    );

    const bounds = extendsViewBox
      ? bbox
      : { x: vbX, y: vbY, width: vbW, height: vbH };

    meshBoundsRef.current = bounds;
    meshEpsilonRef.current = Math.max(bounds.width, bounds.height) * 1e-5;
  }, []);

  /**
   * Handle pattern dragging within ring path container
   * Both pattern and control overlay move together maintaining alignment
   */
  const handlePatternDrag = useCallback(() => {
    if (!svgElementRef.current || !svgContainerRef.current) return;

    const handleMouseDown = (e) => {
      if (!isDragMode) return; // Only drag when drag mode is active

      e.preventDefault();
      e.stopPropagation(); // Prevent control point dragging

      isDraggingPattern.current = true;

      // Store start in both coordinate systems (viewport + pattern) for zoom-aware delta
      dragStartPos.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        patternX: patternPositionRef.current.x,
        patternY: patternPositionRef.current.y
      };
    };

    const handleMouseMove = (e) => {
      if (!isDraggingPattern.current) return;

      const zoom = Math.max(0.01, zoomRef.current);
      // Delta: screen px → container px. patternPosition is in container space; zoom scales container.
      // patternScale does NOT affect translate coords (scale applied after translate).
      const deltaX = (e.clientX - dragStartPos.current.clientX) / zoom;
      const deltaY = (e.clientY - dragStartPos.current.clientY) / zoom;

      const newX = dragStartPos.current.patternX + deltaX;
      const newY = dragStartPos.current.patternY + deltaY;

      const next = { x: newX, y: newY };
      patternPositionRef.current = next;
      /**
       * This is done to avoid re-renders on every mousemove
       * setPatternPosition is called in handleMouseUp to sync the state with the DOM
       * Apply transform directly to DOM during drag to avoid re-renders on every mousemove
       */
      // setPatternPosition(next);
      if (svgElementRef.current && svgControlRef.current) {
        const scale = patternScaleRef.current;
        const transform = `translate(${next.x}px, ${next.y}px) scale(${scale})`;
        svgElementRef.current.style.transform = transform;
        svgControlRef.current.style.transform = transform;
      }

    };

    const handleMouseUp = () => {
      isDraggingPattern.current = false;
      // Sync state once so React and history have the final position (no re-renders during drag)
      setPatternPosition(patternPositionRef.current);

      // Add position change to history after a short delay (avoids hit on every mousemove; captures final state from refs)
      clearPatternDragHistoryTimeout();

      patternDragHistoryTimeoutRef.current = setTimeout(() => {
        console.log('pushing to history >>>>> DRAG PATTERN', isDragMode);
        if (isDragMode) {
          pushToHistory();
        }
      }, 150);
    };

    // Attach event listeners
    const element = svgElementRef.current;
    element.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      clearPatternDragHistoryTimeout();

      element.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragMode]);

  // Parse SVG String
  const parseSVGString = useCallback((svgStr) => {
    try {
      const svgDOM = new DOMParser()
        .parseFromString(svgStr, 'image/svg+xml')
        .getElementsByTagName('svg')[0];

      if (!svgDOM) {
        console.error('Failed to parse SVG string');
        return;
      }

      // Calculate container dimensions first
      calculateContainerDimensions();

      /**
       * Get original SVG dimensions (prefer viewBox, then width/height attributes)
       * Store viewBox dimensions for control point generation
       * Scale display dimensions if width > 1467, but keep original viewBox
       */
      let WIDTH, HEIGHT, viewBoxString;

      if (svgDOM.attributes.viewBox) {
        const [, , vWidth, vHeight] = svgDOM.attributes.viewBox.value.trim().split(/[\s,]+/);
        WIDTH = Number(vWidth);
        HEIGHT = Number(vHeight);
        viewBoxString = svgDOM.attributes.viewBox.value;
      } else {
        WIDTH = svgDOM.attributes.width ? Number(svgDOM.attributes.width.value.replace(/^.*?(\d+(?:\.\d+)?).*/, '$1')) : 500;
        HEIGHT = svgDOM.attributes.height ? Number(svgDOM.attributes.height.value.replace(/^.*?(\d+(?:\.\d+)?).*/, '$1')) : 500;
        viewBoxString = `0 0 ${WIDTH} ${HEIGHT}`;
      }

      // Calculate scaled display dimensions if width > 1167
      if (WIDTH > 1167) {
        viewBoxScaleRef.current = 1167 / WIDTH;
        WIDTH = WIDTH * viewBoxScaleRef.current;
        HEIGHT = HEIGHT * viewBoxScaleRef.current;
      }

      // Set viewBox (keep original coordinate system) and scaled display dimensions
      svgElementRef.current.setAttribute('viewBox', viewBoxString);
      svgElementRef.current.setAttribute('width', `${WIDTH}`);
      svgElementRef.current.setAttribute('height', `${HEIGHT}`);
      svgElementRef.current.setAttribute('fill', svgDOM.attributes.fill?.value || 'inherit');
      svgElementRef.current.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svgElementRef.current.innerHTML = svgDOM.innerHTML.toString();

      widthRef.current = WIDTH;
      heightRef.current = HEIGHT;

      // After SVG is rendered, calculate dimensions and match svg-control
      // Use a small delay to ensure SVG is rendered
      setTimeout(() => {
        calculateContainerDimensions();
        updateMeshBounds();
      }, 0);

    } catch (error) {
      console.error('Error parsing SVG string:', error);
    }
  }, [calculateContainerDimensions, updateMeshBounds]);

  // Initialize warp
  const init = useCallback((restoreControlPoints = null, preserveZoom = false, pushToHistoryWhenDone = false) => {
    if (!svgElementRef.current || !svgControlRef.current || !zoomElementRef.current || !appRef.current) return;

    // Set transform-origin once - it persists for all subsequent transforms
    svgElementRef.current.style.transformOrigin = 'center';
    svgControlRef.current.style.transformOrigin = 'center';

    // Parse SVG string (this will calculate container dimensions after SVG is rendered)
    parseSVGString(svgString);

    // Wait for SVG to render and dimensions to be calculated
    setTimeout(() => {
      if (!svgElementRef.current || !svgControlRef.current) return;


      if (!preserveZoom && zoomElementRef.current) {
        zoomElementRef.current.style.transform = 'scale(1)';
        zoomRef.current = 1;
      }

        // Ensure dimensions are calculated
        calculateContainerDimensions();

        // Flatten path transforms so all path coordinates are in viewBox space.
        // Otherwise paths inside <g transform="..."> deform differently from the mesh (black/white move inconsistently).
        flattenSvgTransforms(svgElementRef.current);
        updateMeshBounds();

      // Clean up previous warp instance
      if (warpRef.current) {
        warpRef.current = null;
      }

      const warp = new Warp(svgElementRef.current);
      warpRef.current = warp;
      const interpLevel = iterpritateSmoothness(interpolationLevelRef.current);
      if (interpLevel !== null) {
        warp.interpolate(interpLevel);
      }
      /**
       * Always generate fresh rectangular mesh points for initial load
       * CRITICAL: We must use the regular rectangular mesh for the initial weight calculation.
       * If we use the saved (distorted) points for weight calculation, the mapping will be wrong (squeezed).
       */
      const meshBounds = meshBoundsRef.current;
      const meshWidth = meshBounds
        ? meshBounds.width
        : widthRef.current * (1 / viewBoxScaleRef.current);
      const meshHeight = meshBounds
        ? meshBounds.height
        : heightRef.current * (1 / viewBoxScaleRef.current);
      const edgeEpsilon = meshEpsilonRef.current || Math.max(meshWidth, meshHeight) * 1e-5;

      let controlPoints = generateMeshPoints(
        meshWidth,
        meshHeight,
        Number(complexityLevelRef.current)
      );

      if (meshBounds) {
        controlPoints = controlPoints.map(([x, y]) => [x + meshBounds.x, y + meshBounds.y]);
      }
      controlPointsRef.current = controlPoints;

      // Transform function - uses current control points from ref
      warp.transform(function (v0) {
        const V = controlPointsRef.current || controlPoints;
        // Avoid MVC singularities for points on vertices or edges.
        for (let i = 0; i < V.length; i++) {
          const dx = v0[0] - V[i][0];
          const dy = v0[1] - V[i][1];
          if (Math.hypot(dx, dy) <= edgeEpsilon) {
            const L = Array(V.length).fill(0);
            L[i] = 1;
            return [...v0, ...L];
          }
        }

        for (let i = 0; i < V.length; i++) {
          const j = (i + 1) % V.length;
          const vi = V[i];
          const vj = V[j];
          const segX = vj[0] - vi[0];
          const segY = vj[1] - vi[1];
          const segLen2 = segX * segX + segY * segY;
          if (segLen2 === 0) continue;

          const t = ((v0[0] - vi[0]) * segX + (v0[1] - vi[1]) * segY) / segLen2;
          if (t < 0 || t > 1) continue;

          const projX = vi[0] + t * segX;
          const projY = vi[1] + t * segY;
          const dist = Math.hypot(v0[0] - projX, v0[1] - projY);
          if (dist <= edgeEpsilon) {
            const L = Array(V.length).fill(0);
            L[i] = 1 - t;
            L[j] = t;
            return [...v0, ...L];
          }
        }

        const A = [];
        const W = [];
        const L = [];

        for (let i = 0; i < V.length; i++) {
          const j = (i + 1) % V.length;
          const vi = V[i];
          const vj = V[j];

          const r0i = Math.sqrt((v0[0] - vi[0]) ** 2 + (v0[1] - vi[1]) ** 2);
          const r0j = Math.sqrt((v0[0] - vj[0]) ** 2 + (v0[1] - vj[1]) ** 2);
          const rij = Math.sqrt((vi[0] - vj[0]) ** 2 + (vi[1] - vj[1]) ** 2);

          const dn = 2 * r0i * r0j;
          if (dn === 0) {
            A[i] = 0;
            continue;
          }
          const r = (r0i ** 2 + r0j ** 2 - rij ** 2) / dn;

          A[i] = isNaN(r) ? 0 : Math.acos(Math.max(-1, Math.min(r, 1)));
        }

        for (let j = 0; j < V.length; j++) {
          const i = (j > 0 ? j : V.length) - 1;
          const vj = V[j];
          const r = Math.sqrt((vj[0] - v0[0]) ** 2 + (vj[1] - v0[1]) ** 2);
          if (r === 0) {
            W[j] = 0;
          } else {
            W[j] = (Math.tan(A[i] / 2) + Math.tan(A[j] / 2)) / r;
          }
        }

        const Ws = W.reduce((a, b) => a + b, 0);
        if (Ws === 0) {
          return [...v0, ...Array(V.length).fill(0)];
        }
        for (let i = 0; i < V.length; i++) {
          L[i] = W[i] / Ws;
        }

        return [...v0, ...L];
      });

      // Reposition function - uses current control points from ref
      const reposition = ([x, y, ...W]) => {
        const V = controlPointsRef.current || controlPoints;
        let nx = 0;
        let ny = 0;

        for (let i = 0; i < V.length; i++) {
          nx += W[i] * V[i][0];
          ny += W[i] * V[i][1];
        }

        return [nx, ny, ...W];
      };

      // Draw control shape
      const drawControlShape = (element, V = controlPoints) => {
        if (!element) return;
        const path = [`M${V[0][0]} ${V[0][1]}`];
        for (let i = 1; i < V.length; i++) {
          path.push(`L${V[i][0]} ${V[i][1]}`);
        }
        path.push('Z');
        element.setAttribute('d', path.join(''));
      };

      // Draw control points
      const drawControlPoints = (element, V = controlPoints) => {
        if (!element || !svgControlRef.current) return;

        // Clear existing points
        draggableControlPointsRef.current.forEach(point => {
          if (point && point.parentNode) {
            point.parentNode.removeChild(point);
          }
        });
        draggableControlPointsRef.current = [];

        // Helper to convert mouse/touch event to SVG coordinates
        const getSvgCoords = (event) => {
          const svg = svgControlRef.current;
          if (!svg) return { x: 0, y: 0 };

          let clientX;
          let clientY;

          if (event.touches && event.touches[0]) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
          } else {
            clientX = event.clientX;
            clientY = event.clientY;
          }

          // Use SVG's CTM for accurate coordinate transformation
          const point = svg.createSVGPoint();
          point.x = clientX;
          point.y = clientY;

          const ctm = svg.getScreenCTM();
          if (ctm) {
            try {
              const svgPoint = point.matrixTransform(ctm.inverse());
              return { x: svgPoint.x, y: svgPoint.y };
            } catch (e) {
              // Fallback to viewBox + bounding rect mapping
              const viewBox = svg.getAttribute('viewBox');
              const rect = svg.getBoundingClientRect();
              if (viewBox && rect.width && rect.height) {
                const [vx, vy, vWidth, vHeight] = viewBox.split(' ').map(Number);
                const x = ((clientX - rect.left) / rect.width) * vWidth + vx;
                const y = ((clientY - rect.top) / rect.height) * vHeight + vy;
                return { x, y };
              }
            }
          }

          // Last-resort fallback: use bounding rect only
          const rect = svg.getBoundingClientRect();
          return {
            x: clientX - rect.left,
            y: clientY - rect.top,
          };
        };

        // Calculate radius using all three scales: viewBox, zoom, and patternScale
        const finalRadius = CIRCLE_RADIUS / getCombinedScale();

        V.forEach((point, index) => {
          const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circle.setAttributeNS(null, 'class', 'control-point');
          circle.setAttributeNS(null, 'cx', point[0]);
          circle.setAttributeNS(null, 'cy', point[1]);
          circle.setAttributeNS(null, 'r', finalRadius);
          element.appendChild(circle);

          draggableControlPointsRef.current.push(circle);

          // Native drag: mousedown + mousemove + mouseup
          const handleMouseDown = (downEvent) => {
            downEvent.preventDefault();

            const handleMove = (moveEvent) => {
              if (!svgControlRef.current || !warpRef.current) return;

              const currentPoints = controlPointsRef.current || [];
              const { x, y } = getSvgCoords(moveEvent);

              // Update circle position and control point
              circle.setAttribute('cx', x);
              circle.setAttribute('cy', y);
              currentPoints[index] = [x, y];
              controlPointsRef.current = currentPoints;

              // Redraw control path
              if (controlPathRef.current) {
                drawControlShape(controlPathRef.current, currentPoints);
              }

              // Apply warp
              warpRef.current.transform(reposition);
            };

            const handleUp = () => {
              document.removeEventListener('mousemove', handleMove);
              document.removeEventListener('mouseup', handleUp);
              document.removeEventListener('touchmove', handleMove);
              document.removeEventListener('touchend', handleUp);

               // Save to history after drag completes
              if (controlPointsRef.current) {
                console.log('pushing to history >>>>> DRAG CONTROL POINT');
                pushToHistory();
              }
            };

            document.addEventListener('mousemove', handleMove);
            document.addEventListener('mouseup', handleUp);
            document.addEventListener('touchmove', handleMove, { passive: false });
            document.addEventListener('touchend', handleUp);
          };

          circle.addEventListener('mousedown', handleMouseDown);
          circle.addEventListener('touchstart', handleMouseDown, { passive: false });
        });
      };

      // Use the existing control path from React ref, or create one if it doesn't exist
      if (!controlPathRef.current && svgControlRef.current) {
        const existingPath = svgControlRef.current.querySelector('#control-path');
        if (existingPath) {
          controlPathRef.current = existingPath;
        } else {
          const newControlPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          newControlPath.setAttributeNS(null, 'id', 'control-path');
          svgControlRef.current.appendChild(newControlPath);
          controlPathRef.current = newControlPath;
        }
      }
      /**
       * Before applying the warp, we need to check if we have saved points
       * If we have saved points, we need to restore them
       * If we don't have saved points, we need to create new points
       * After draw control points / control shape / control path, we need to apply the warp
       */
      if (restoreControlPoints && Array.isArray(restoreControlPoints) && restoreControlPoints.length > 0) {
        console.log('Restoring saved control points:', restoreControlPoints.length);
        const restoredPoints = restoreControlPoints.map(point => Array.isArray(point) ? [...point] : point);
        controlPointsRef.current = restoredPoints;
      }
      const finalPoints = controlPointsRef.current;

      if (controlPathRef.current) {
        drawControlShape(controlPathRef.current, finalPoints);
      }
      drawControlPoints(svgControlRef.current, finalPoints);

      // Apply Warp transformation (Uses controlPointsRef.current internally via reposition)
      warp.transform(reposition);

      // Store state in history: initial load, or after mesh-complexity/interpolation change (new control points + current settings)
      if (historyRef.current.length === 0 || pushToHistoryWhenDone) {
        if (pushToHistoryWhenDone) {
          console.log('pushing to history >>>>> MESH/INTERPOLATION CHANGE');
        } else {
          console.log('pushing to history >>>>> INIT');
        }
        pushToHistory();
      }
    }, 100); // Wait 100ms for SVG to render and dimensions to be calculated
  }, [svgString, parseSVGString, calculateContainerDimensions, updateMeshBounds, syncUndoRedoState]);

  // Reset manual warp: clear position/scale, clear history, re-init mesh, then sync reducer
  const resetManualWarp = useCallback(() => {
    clearPatternDragHistoryTimeout();
    clearPatternScaleHistoryTimeout();
    setPatternPosition({ x: 0, y: 0 });
    setPatternScale(1);
    patternPositionRef.current = { x: 0, y: 0 };
    patternScaleRef.current = 1;
    historyRef.current = [];
    historyIndexRef.current = -1;
    syncUndoRedoState();
    init(null);
    dispatch({ type: ACTIONS.RESET_MANUAL_WARP_DATA });
  }, [dispatch, init, syncUndoRedoState]);

  const onDialogClose = useCallback((confirmed) => {
    setShowResetConfirm(false);
    if (confirmed) {
      resetManualWarp();
    }
  }, [resetManualWarp]);

  // Calculate container dimensions on mount and resize
  useEffect(() => {
    let resizeTimeout;
    let lastWidth = 0;
    let lastHeight = 0;

    const handleResize = () => {
      if (!appRef.current) return;

      const containerRect = appRef.current.getBoundingClientRect();
      const currentWidth = Math.round(containerRect.width);
      const currentHeight = Math.round(containerRect.height);

      // Only proceed if dimensions actually changed significantly (more than 5px)
      if (Math.abs(currentWidth - lastWidth) < 5 && Math.abs(currentHeight - lastHeight) < 5) {
        return;
      }

      lastWidth = currentWidth;
      lastHeight = currentHeight;

      // Debounce resize handling
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        try {
          calculateContainerDimensions();
          // Only update viewBox, don't re-init everything
          if (svgControlRef.current && widthRef.current && heightRef.current) {
            svgControlRef.current.setAttribute('viewBox', `0 0 ${widthRef.current} ${heightRef.current}`);
            // Update control points if they exist
            if (controlPointsRef.current && controlPointsRef.current.length > 0) {
              const drawControlShape = (element, V) => {
                if (!element) return;
                const path = [`M${V[0][0]} ${V[0][1]}`];
                for (let i = 1; i < V.length; i++) {
                  path.push(`L${V[i][0]} ${V[i][1]}`);
                }
                path.push('Z');
                element.setAttribute('d', path.join(''));
              };
              if (controlPathRef.current) {
                drawControlShape(controlPathRef.current, controlPointsRef.current);
              }
            }
          }
        } catch (error) {
          console.error('Error during resize:', error);
        }
      }, 200); // 200ms debounce
    };

    // Calculate initial dimensions
    calculateContainerDimensions();
    if (appRef.current) {
      const rect = appRef.current.getBoundingClientRect();
      lastWidth = Math.round(rect.width);
      lastHeight = Math.round(rect.height);
    }

    // Listen for window resize events only (more stable)
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateContainerDimensions]);

  /**
   * Dont add init to the dependency array because it will cause issue on mesh complexity change
   */
  useEffect(() => {
    if(savedControlPoints && savedControlPoints.length > 0) {
      setTimeout(() => {
        init(savedControlPoints);
      })
    }
  }, [savedControlPoints]);

  // Restore saved history (entries as { controlPoints: [], patternPosition?, patternScale?, meshComplexity?, interpolation? }; supports legacy array format)
  useEffect(() => {
    if (savedHistory && Array.isArray(savedHistory) && savedHistory.length > 0) {
      const restoredHistory = savedHistory.map((entry) => {
        const points = Array.isArray(entry) ? entry : (entry.controlPoints || []);
        const controlPoints = points.map(p => [...p]);
        const patternPosition = !Array.isArray(entry) && entry.patternPosition != null
          ? { x: entry.patternPosition.x, y: entry.patternPosition.y }
          : undefined;
        const patternScale = !Array.isArray(entry) && entry.patternScale != null ? entry.patternScale : undefined;
        const meshComplexity = !Array.isArray(entry) && entry.meshComplexity != null ? entry.meshComplexity : undefined;
        const interpolation = !Array.isArray(entry) && entry.interpolation != null ? entry.interpolation : undefined;
        return { controlPoints, patternPosition, patternScale, meshComplexity, interpolation };
      });
      historyRef.current = restoredHistory;
      const len = restoredHistory.length;
      // Clamp index to [0, len-1]; index 0 is initial state, no -1
      historyIndexRef.current = Math.max(0, Math.min(
        savedHistoryIndex ?? len - 1,
        len - 1
      ));
      syncUndoRedoState();
    }
  }, [savedHistory, savedHistoryIndex, syncUndoRedoState]);

  // Re-init when SVG string or settings change
  useEffect(() => {
    if (isRestoringFromHistoryRef.current) {
      isRestoringFromHistoryRef.current = false;
      return;
    }
    if (svgElementRef.current && svgControlRef.current) {
      // Clear existing control points
      draggableControlPointsRef.current.forEach(point => {
        if (point && point.parentNode) {
          point.parentNode.removeChild(point);
        }
      });
      draggableControlPointsRef.current = [];

      // Clean up existing control path if it exists
      if (controlPathRef.current && controlPathRef.current.parentNode) {
        controlPathRef.current.parentNode.removeChild(controlPathRef.current);
        controlPathRef.current = null;
      }

      init();
    }
  }, [svgString, init]);

  // Helper function to update control point radius based on all three scales
  const updateControlPointRadius = useCallback(() => {
     // Calculate radius using all three scales: viewBox, zoom, and patternScale
     const finalRadius = CIRCLE_RADIUS / getCombinedScale();
    draggableControlPointsRef.current.forEach((point) => {
      if (point) {
        point.setAttribute('r', finalRadius);
      }
    });
  }, []);

  // Zoom handler - handles both zoom and scale mode
  useEffect(() => {
    const handleWheel = (e) => {
      if (!controlPathRef.current || !zoomElementRef.current) return;

      // If scale mode is active, scale the pattern instead of zooming
      if (isScaleMode) {
        e.preventDefault();
        let newScale = patternScaleRef.current;

        // Scale factor: scroll down = smaller, scroll up = larger
        // Limit scale between 0.1 and 3.0
        if (e.deltaY > 0) {
          newScale = Math.max(0.1, patternScaleRef.current - 0.02);
        } else {
          newScale = Math.min(3.0, patternScaleRef.current + 0.02);
        }

        patternScaleRef.current = newScale;
        setPatternScale(newScale);

        // Apply scale transform to pattern and control
        if (svgElementRef.current && svgControlRef.current) {
          const transform = `translate(${patternPosition.x}px, ${patternPosition.y}px) scale(${newScale})`;
          svgElementRef.current.style.transform = transform;
          svgControlRef.current.style.transform = transform;
        }

        // Debounced push to history (same pattern as pattern drag)
        clearPatternScaleHistoryTimeout();
        patternScaleHistoryTimeoutRef.current = setTimeout(() => {
          console.log('pushing to history >>>>> SCALE');
          pushToHistory();
        }, 150);
        return;
      }

      // Normal zoom behavior when scale mode is not active
      let newZoom = zoomRef.current;

      if (e.deltaY > 0) {
        newZoom = zoomRef.current + 0.02;
      } else if (zoomElementRef.current.getBoundingClientRect().width >= 30) {
        newZoom = zoomRef.current - 0.02;
      } else {
        return;
      }

      zoomRef.current = newZoom;
      zoomElementRef.current.style.transform = `scale(${newZoom})`;
      if (controlPathRef.current) {
        controlPathRef.current.style.strokeWidth = `${1 / newZoom}px`;
      }
      // Update control point radius based on all scales (viewBox, zoom, patternScale)
      updateControlPointRadius();
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      clearPatternScaleHistoryTimeout();
    };
  }, [isScaleMode, patternPosition, updateControlPointRadius]);

  // Canvas move handler
  useEffect(() => {
    if (svgContainerRef.current) {
      try {
        moveCanvasHandler(svgContainerRef.current);
      } catch (error) {
        console.error('Error setting up canvas move handler:', error);
      }
    }
  }, []);

  // Dropzone handler
  // TODO: Implement dropzone functionality when needed
  // useEffect(() => {
  //   const handleFileDrop = (fileContent) => {
  //     setSvgString(fileContent);
  //   };
  // }, []);

  // Apply pattern position and scale transform to both pattern and control
  useEffect(() => {
    if (svgElementRef.current && svgControlRef.current) {
      const transform = `translate(${patternPosition.x}px, ${patternPosition.y}px) scale(${patternScale})`;
      svgElementRef.current.style.transform = transform;
      svgControlRef.current.style.transform = transform;

      // Update cursor based on drag mode or scale mode
      if (isDragMode) {
        svgElementRef.current.style.cursor = 'move';
        svgControlRef.current.style.pointerEvents = 'none'; // Disable control points in drag mode
      } else if (isScaleMode) {
        svgElementRef.current.style.cursor = 'zoom-in';
        svgControlRef.current.style.pointerEvents = 'none'; // Disable control points in scale mode
      } else {
        svgElementRef.current.style.cursor = 'default';
        svgControlRef.current.style.pointerEvents = 'auto'; // Enable control points
      }
    }
  }, [patternPosition, patternScale, isDragMode, isScaleMode]);

  // Hide/show control points based on scale mode
  useEffect(() => {
    // Hide control points and control path when scale mode is active
    if (isScaleMode || isDragMode) {
      // Hide control points
      draggableControlPointsRef.current.forEach((point) => {
        if (point) {
          point.style.display = 'none';
        }
      });
      // Hide control path
      if (controlPathRef.current) {
        controlPathRef.current.style.display = 'none';
      }
    } else {
      // Show control points
      draggableControlPointsRef.current.forEach((point) => {
        if (point) {
          point.style.display = '';
        }
      });
      // Show control path
      if (controlPathRef.current) {
        controlPathRef.current.style.display = '';
      }
      // Update control point radius when showing (based on all scales: viewBox, zoom, patternScale)
      // Use setTimeout to ensure DOM is updated before calculating radius
      setTimeout(() => {
        if (draggableControlPointsRef.current.length > 0) {
          updateControlPointRadius();
        }
      }, 0);
    }
  }, [isScaleMode, isDragMode, updateControlPointRadius]);

  // Setup pattern drag handlers
  useEffect(() => {
    return handlePatternDrag();
  }, [handlePatternDrag]);

  /* const applyPatternPositionTransform = (svg) => {
    if (patternPosition.x !== 0 || patternPosition.y !== 0) {
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('transform', `translate(${patternPosition.x}, ${patternPosition.y})`);
      while (svg.firstChild) {
        g.appendChild(svg.firstChild);
      }
      svg.appendChild(g);
    }
  }*/

  /**
   * Apply viewBox scale to shrink SVG and scale down all paths
   * This scales the SVG dimensions and all path coordinates
   */
  /*const applyViewBoxScale = (svg, scale) => {
    if (scale === 1) return; // No scaling needed

    // Get current dimensions
    const currentWidth = parseFloat(svg.getAttribute('width')) || 500;
    const currentHeight = parseFloat(svg.getAttribute('height')) || 500;
    const viewBox = svg.getAttribute('viewBox');

    // Scale down width and height
    const newWidth = currentWidth * scale;
    const newHeight = currentHeight * scale;
    svg.setAttribute('width', newWidth.toString());
    svg.setAttribute('height', newHeight.toString());

    // Scale down viewBox if it exists (only scale width and height, keep x and y the same)
    if (viewBox) {
      const parts = viewBox.trim().split(/[\s,]+/).map(Number);
      if (parts.length >= 4) {
        const [x, y, w, h] = parts;
        svg.setAttribute('viewBox', `${x} ${y} ${w * scale} ${h * scale}`);
      }
    }

    // Scale down all paths and other elements with coordinates
    const scalePath = (pathData) => {
      if (!pathData) return pathData;
      // Match path commands and their coordinates
      return pathData.replace(/([MLHVCSQTAZ])([^MLHVCSQTAZ]*)/gi, (match, command, coords) => {
        if (command.toUpperCase() === 'Z') return command;
        // Parse and scale coordinates
        const numbers = coords.trim().split(/[\s,]+/).filter(Boolean).map(Number);
        const scaled = numbers.map(n => isNaN(n) ? n : n * scale);
        return command + scaled.join(' ');
      });
    };

    // Scale all path elements
    const paths = svg.querySelectorAll('path');
    paths.forEach(path => {
      const d = path.getAttribute('d');
      if (d) {
        path.setAttribute('d', scalePath(d));
      }
    });

    // Scale circle elements
    const circles = svg.querySelectorAll('circle');
    circles.forEach(circle => {
      const cx = parseFloat(circle.getAttribute('cx'));
      const cy = parseFloat(circle.getAttribute('cy'));
      const r = parseFloat(circle.getAttribute('r')) * (1 / viewBoxScaleRef.current);

      if (!isNaN(cx)) circle.setAttribute('cx', (cx * scale).toString());
      if (!isNaN(cy)) circle.setAttribute('cy', (cy * scale).toString());
      if (!isNaN(r)) circle.setAttribute('r', (r * scale).toString());
    });

    // Scale rect elements
    const rects = svg.querySelectorAll('rect');
    rects.forEach(rect => {
      const x = parseFloat(rect.getAttribute('x'));
      const y = parseFloat(rect.getAttribute('y'));
      const width = parseFloat(rect.getAttribute('width'));
      const height = parseFloat(rect.getAttribute('height'));
      if (!isNaN(x)) rect.setAttribute('x', (x * scale).toString());
      if (!isNaN(y)) rect.setAttribute('y', (y * scale).toString());
      if (!isNaN(width)) rect.setAttribute('width', (width * scale).toString());
      if (!isNaN(height)) rect.setAttribute('height', (height * scale).toString());
    });

    // Scale ellipse elements
    const ellipses = svg.querySelectorAll('ellipse');
    ellipses.forEach(ellipse => {
      const cx = parseFloat(ellipse.getAttribute('cx'));
      const cy = parseFloat(ellipse.getAttribute('cy'));
      const rx = parseFloat(ellipse.getAttribute('rx'));
      const ry = parseFloat(ellipse.getAttribute('ry'));
      if (!isNaN(cx)) ellipse.setAttribute('cx', (cx * scale).toString());
      if (!isNaN(cy)) ellipse.setAttribute('cy', (cy * scale).toString());
      if (!isNaN(rx)) ellipse.setAttribute('rx', (rx * scale).toString());
      if (!isNaN(ry)) ellipse.setAttribute('ry', (ry * scale).toString());
    });

    // Scale line elements
    const lines = svg.querySelectorAll('line');
    lines.forEach(line => {
      const x1 = parseFloat(line.getAttribute('x1'));
      const y1 = parseFloat(line.getAttribute('y1'));
      const x2 = parseFloat(line.getAttribute('x2'));
      const y2 = parseFloat(line.getAttribute('y2'));
      if (!isNaN(x1)) line.setAttribute('x1', (x1 * scale).toString());
      if (!isNaN(y1)) line.setAttribute('y1', (y1 * scale).toString());
      if (!isNaN(x2)) line.setAttribute('x2', (x2 * scale).toString());
      if (!isNaN(y2)) line.setAttribute('y2', (y2 * scale).toString());
    });

    // Scale polyline and polygon elements
    const polylines = svg.querySelectorAll('polyline, polygon');
    polylines.forEach(poly => {
      const points = poly.getAttribute('points');
      if (points) {
        const scaledPoints = points.split(/[\s,]+/).map((val, idx) => {
          const num = parseFloat(val);
          return isNaN(num) ? val : (num * scale).toString();
        }).join(' ');
        poly.setAttribute('points', scaledPoints);
      }
    });
  };*/

 /**
  * Wraps SVG content in a <g> element with transform to prevent clipping
  * when content exceeds the viewBox boundaries.
  * Does NOT modify the SVG's viewBox, width, or height attributes.
  *
  * @param {SVGSVGElement} svg - The SVG element to process
  */
 /*
  * ******* Below is commented out because we are not using warp Offset anymore******
  const wrapSvgContentInGroup = (svg) => {
    if (!(svg instanceof SVGSVGElement)) {
      console.warn('wrapSvgContentInGroup: Expected an SVGSVGElement');
      return;
    }

    // Get viewBox dimensions
    const viewBox = svg.viewBox.baseVal;
    let viewBoxX = 0;
    let viewBoxY = 0;
    let viewBoxWidth = 0;
    let viewBoxHeight = 0;

    if (viewBox && viewBox.width && viewBox.height) {
      viewBoxX = viewBox.x;
      viewBoxY = viewBox.y;
      viewBoxWidth = viewBox.width;
      viewBoxHeight = viewBox.height;
    } else {
      // Fallback to width/height attributes if no viewBox
      const width = parseFloat(svg.getAttribute('width')) || 500;
      const height = parseFloat(svg.getAttribute('height')) || 500;
      viewBoxWidth = width;
      viewBoxHeight = height;
    }

    const svgNS = "http://www.w3.org/2000/svg";

    // Check if content is already wrapped (avoid double-wrapping)
    const existingWrapper = svg.querySelector('g[data-svg-wrapper="true"]');
    if (existingWrapper) {
      // Remove existing wrapper and move its children back
      while (existingWrapper.firstChild) {
        svg.appendChild(existingWrapper.firstChild);
      }
      svg.removeChild(existingWrapper);
    }

    // Create wrapper group
    const wrapperGroup = document.createElementNS(svgNS, "g");
    wrapperGroup.setAttribute('data-svg-wrapper', 'true');

    // Collect all direct children (defs, style, etc. stay at root level)
    const childrenToWrap = [];
    const childrenToKeep = [];

    Array.from(svg.childNodes).forEach(child => {
      // Keep defs, style, script, title, desc, metadata at root level
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tagName = child.tagName?.toLowerCase();
        if (tagName === 'defs' || tagName === 'style' || tagName === 'script' ||
            tagName === 'title' || tagName === 'desc' || tagName === 'metadata') {
          childrenToKeep.push(child);
        } else {
          childrenToWrap.push(child);
        }
      } else if (child.nodeType === Node.TEXT_NODE && child.textContent.trim()) {
        // Keep non-empty text nodes (comments, etc.)
        childrenToKeep.push(child);
      } else if (child.nodeType === Node.COMMENT_NODE) {
        childrenToKeep.push(child);
      } else {
        childrenToWrap.push(child);
      }
    });

    // Move content elements into wrapper group
    childrenToWrap.forEach(child => {
      wrapperGroup.appendChild(child);
    });

    // Append wrapper group to SVG (after defs, style, etc.)
    if (wrapperGroup.firstChild) {
      svg.appendChild(wrapperGroup);
    }

    // Measure real bounds of content (need to temporarily show it)
    // Create a temporary clone to measure without affecting the original
    const tempSvg = svg.cloneNode(true);
    tempSvg.style.position = 'absolute';
    tempSvg.style.visibility = 'hidden';
    tempSvg.style.left = '-9999px';
    tempSvg.style.top = '-9999px';
    tempSvg.removeAttribute('width');
    tempSvg.removeAttribute('height');
    document.body.appendChild(tempSvg);

    let bbox;
    try {
      const tempWrapper = tempSvg.querySelector('g[data-svg-wrapper="true"]');
      if (tempWrapper) {
        bbox = tempWrapper.getBBox();
      } else {
        bbox = tempSvg.getBBox();
      }
    } catch (e) {
      console.warn('Could not get bounding box, using viewBox dimensions', e);
      bbox = {
        x: viewBoxX,
        y: viewBoxY,
        width: viewBoxWidth,
        height: viewBoxHeight
      };
    }

    document.body.removeChild(tempSvg);

    // Without scale, just calculate translate to center content
    const bboxCenterX = bbox.x + bbox.width / 2;
    const bboxCenterY = bbox.y + bbox.height / 2;
    const viewBoxCenterX = viewBoxWidth / 2;
    const viewBoxCenterY = viewBoxHeight / 2;

    let translateX = viewBoxCenterX - bboxCenterX;
    let translateY = viewBoxCenterY - bboxCenterY;

    // Clamp translate values to prevent extreme offsets that cause clipping
    // Allow content to shift up to 50% of viewBox dimension in any direction
    const maxTranslate = Math.max(viewBoxWidth, viewBoxHeight) * 0.5;
    translateX = Math.max(-maxTranslate, Math.min(translateX, maxTranslate));
    translateY = Math.max(-maxTranslate, Math.min(translateY, maxTranslate));

    console.log('Wrapper translate (no scale):', {
      bbox,
      viewBox: { viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight },
      translateX,
      translateY
    });

    // Apply transform to wrapper group
    // if (wrapperGroup.firstChild) {
    //   wrapperGroup.setAttribute(
    //     "transform",
    //     `translate(${translateX}, ${translateY})`
    //   );
    // }

    // Set overflow visible to prevent clipping
    svg.setAttribute('overflow', 'visible');

    return { warpScale: 1, warpTranslateX: translateX, warpTranslateY: translateY };
  };*/

 /**
  * Save warped svg / control points / mesh complexity / interpolation level
  * Apply pattern position transform to actual SVG structure before saving
  */
  const handleSave = () => {
    if (svgElementRef.current && onSave) {
      const warpedSvg = svgElementRef.current.cloneNode(true);

      // Wrap content in <g> with transform to prevent clipping when content exceeds viewBox
      // const { warpScale, warpTranslateX, warpTranslateY } = wrapSvgContentInGroup(warpedSvg);

      /**
       * Apply pattern position transform to actual SVG structure before saving
       * It moved in preview variant card no need here
       */
      // applyPatternPositionTransform(warpedSvg);
      const svgString = new XMLSerializer().serializeToString(warpedSvg);
      const currentControlPoints = controlPointsRef.current ? [...controlPointsRef.current] : null;

      // Save history as array of { controlPoints: [], patternPosition: { x, y }, patternScale?, meshComplexity?, interpolation? }
      const currentHistory = historyRef.current.length > 0
        ? historyRef.current.map((entry) => {
            const points = (entry.controlPoints ?? entry).map(p => [...p]);
            const pos = entry.patternPosition ?? null;
            const scale = entry.patternScale ?? null;
            const mesh = entry.meshComplexity ?? null;
            const interp = entry.interpolation ?? null;
            return {
              controlPoints: points,
              patternPosition: pos != null ? { x: pos.x, y: pos.y } : undefined,
              patternScale: scale,
              meshComplexity: mesh ?? undefined,
              interpolation: interp ?? undefined,
            };
          })
        : null;
      const warpData = {
        meshComplexity: complexityLevelRef.current,
        interpolation: interpolationLevelRef.current,
        controlPoints: currentControlPoints,
        svgString: svgString,
        patternPosition: patternPosition,
        patternScale: patternScale,
        viewBoxScale: viewBoxScaleRef.current,
        history: currentHistory,
        historyIndex: historyIndexRef.current ?? -1,
        // warpOffset: { warpTranslateX, warpTranslateY, warpScale },
      }
      onSave(svgString, warpData);
    } else if (svgElementRef.current) {
      saveResultHandler(svgElementRef.current);
    }
  };

    // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z or Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl+Y or Cmd+Y or Ctrl+Shift+Z for redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  return (
    <div id="app" ref={appRef} className={showOriginalBox ? 'checkerboard-pattern' : ''}>
      {/* Dropzone Preview */}
      <div className="dropzone-preview">
        {/* Your dropzone UI */}
      </div>

      {/* Single Container - Ring path dimensions, centered, contains everything */}
      <div id="scale-wrap" ref={zoomElementRef}>
        <div id="svg-container" ref={svgContainerRef} style={{ overflow: 'visible' }}>
          {/* Ring path SVG - now inside the main container */}
          <svg id="svg-ring-path" ref={svgRingPathRef} style={{ position: 'absolute', pointerEvents: 'none' }}>
            <path id="ring-path" ref={ringPathRef} />
          </svg>
          {/* Control points SVG */}
          <svg id="svg-control" ref={svgControlRef}>
            <path id="control-path" ref={controlPathRef} d="" />
          </svg>
          {/* Pattern SVG */}
          <svg id="svg-element" ref={svgElementRef}></svg>
        </div>
      </div>
      <div className="actions">
        {showSettings && <div className="content-block" id="settings-block">
          <div className="close-btn" onClick={() => setShowSettings(false)}></div>
          <h3>Settings:</h3>
          <div className="range-selector">
            <label htmlFor="mesh-complexity">Mesh complexity</label>
            <input id="mesh-complexity" type="range" min="1" max="7" step="1" value={complexityLevel}
              onChange={updateComplexityLevel}></input>
          </div>
          <div className="range-selector">
            <label htmlFor="interpolation-complexity">Smoothness <span style={{ opacity: 0.5 }}>(interpolationLevel)</span>
            </label>
            <input id="interpolation-complexity" type="range" min="0" max="400" step="100" value={interpolationLevel}
              onChange={updateInterpolationLevel}></input>
          </div>
          <div className="checkbox-block">
            <input id="show-original-box-btn" type="checkbox" checked={showOriginalBox}
            onChange={(e) => setShowOriginalBox(e.target.checked)}></input>
            <label htmlFor="show-original-box-btn">Show original box</label>
          </div>
        </div>}
        <div className="action-buttons utility-buttons">
          <button id="show-settings" className={`secondary-btn ${showSettings ? 'active' : ''}`} onClick={() => setShowSettings(!showSettings)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" stroke="none">
              <path d="M9 3C7.13616 3 5.57006 4.27477 5.12602 6L2 6V8H5.12602C5.57006 9.72523 7.13616 11 9 11C10.8638 11 12.4299 9.72523 12.874 8H22V6L12.874 6C12.4299 4.27477 10.8638 3 9 3ZM7 7C7 8.10457 7.89543 9 9 9C10.1046 9 11 8.10457 11 7C11 5.89543 10.1046 5 9 5C7.89543 5 7 5.89543 7 7Z M11.126 18H2V16H11.126C11.5701 14.2748 13.1362 13 15 13C16.8638 13 18.4299 14.2748 18.874 16H22V18H18.874C18.4299 19.7252 16.8638 21 15 21C13.1362 21 11.5701 19.7252 11.126 18ZM15 19C13.8954 19 13 18.1046 13 17C13 15.8954 13.8954 15 15 15C16.1046 15 17 15.8954 17 17C17 18.1046 16.1046 19 15 19Z"></path>
            </svg>
          </button>
          <button id="save-result-btn" className="primary-btn" onClick={handleSave}>
            Save Result
          </button>
        </div>
      </div>
      <div className="floating-action-bar">
        <button
          id="undo-btn"
          className="secondary-btn"
          onClick={handleUndo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
          style={{ opacity: canUndo ? 1 : 0.5, cursor: canUndo ? 'pointer' : 'not-allowed' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M3 7v6h6"/>
            <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/>
          </svg>
        </button>
        <button
          id="redo-btn"
          className="secondary-btn"
          onClick={handleRedo}
          disabled={!canRedo}
          title="Redo (Ctrl+Y)"
          style={{ opacity: canRedo ? 1 : 0.5, cursor: canRedo ? 'pointer' : 'not-allowed' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M21 7v6h-6"/>
            <path d="M3 17a9 9 0 019-9 9 9 0 016 2.3l3 2.7"/>
          </svg>
        </button>
        <button
          id="toggle-drag-mode"
          className={`secondary-btn ${isDragMode ? 'active' : ''}`}
          onClick={() => {
            setIsDragMode(!isDragMode);
            if (!isDragMode) setIsScaleMode(false); // Disable scale mode when enabling drag mode
          }}
          title={isDragMode ? "Drag Mode: ON (Click to disable)" : "Drag Mode: OFF (Click to enable)"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"/>
          </svg>
        </button>
        <button
          id="toggle-scale-mode"
          className={`secondary-btn ${isScaleMode ? 'active' : ''}`}
          onClick={() => {
            setIsScaleMode(!isScaleMode);
            if (!isScaleMode) setIsDragMode(false); // Disable drag mode when enabling scale mode
          }}
          title={isScaleMode ? "Scale Mode: ON (Scroll to scale, Click to disable)" : "Scale Mode: OFF (Click to enable, then scroll to scale)"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
            <path d="M11 8v6M8 11h6"/>
          </svg>
        </button>
        <button
          id="reset-manual-warp-btn"
          className="secondary-btn"
          onClick={() => setShowResetConfirm(true)}
          disabled={!canUndo && !canRedo}
          title="Reset manual warp"
          style={{ opacity: (canUndo || canRedo) ? 1 : 0.5, cursor: (canUndo || canRedo) ? 'pointer' : 'not-allowed' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
          </svg>
        </button>
      </div>
      <ConfirmDialog
        title="Reset manual warp"
        description="All manual warp changes will be lost. Are you sure you want to reset?"
        open={showResetConfirm}
        onCancel={() => onDialogClose(false)}
        onSuccess={() => onDialogClose(true)}
        cancelLabel="Cancel"
        confirmLabel="Reset"
      />
    </div>
  );
};

export default WarpSvg;
